// JsonNode.tsx
import React, { useState, useEffect, useRef } from "react";

interface JsonNodeProps {
  data: any;
  name?: string;
  level?: number;
  path?: (string | number)[];
  searchTerm?: string;
  highlightPath?: string;
  resultId?: string;
  setResultRef?: (id: string, element: HTMLElement | null) => void;
  onPathClick?: (path: string) => void;
  selectedPath?: string | null;
  isKeyMatch?: boolean;
  isMainSelector?: boolean;
  mainSelectorName?: string;
}

const JsonNode: React.FC<JsonNodeProps> = ({
  data,
  name,
  level = 0,
  path = [],
  searchTerm = "",
  highlightPath,
  resultId,
  setResultRef,
  onPathClick,
  selectedPath,
  isKeyMatch = false,
  isMainSelector = false,
  mainSelectorName,
}) => {
  const [expanded, setExpanded] = useState<boolean>(true);
  const nodeRef = useRef<HTMLDivElement>(null);

  // Construire le chemin actuel sous forme de chaîne
  const currentPath = path
    .map((key, index) =>
      typeof key === "number" ? `[${key}]` : index === 0 ? key : `.${key}`,
    )
    .join("");

  const isHighlighted = highlightPath === currentPath;

  // Vérifier si le nom (clé) correspond au terme de recherche
  const isNameHighlighted =
    searchTerm &&
    name &&
    name.toString().toLowerCase().includes(searchTerm.toLowerCase());

  // Vérifier si la valeur correspond au terme de recherche (pour les valeurs primitives)
  const isValueHighlighted =
    typeof data !== "object" &&
    data !== null &&
    searchTerm &&
    String(data).toLowerCase().includes(searchTerm.toLowerCase());

  // Déterminer si ce nœud doit être référencé pour le défilement
  const shouldSetRef =
    (isHighlighted || isNameHighlighted || isValueHighlighted || isKeyMatch) &&
    resultId &&
    setResultRef;

  // Déterminer la priorité de cet élément pour le défilement
  // Plus la priorité est élevée, plus l'élément est pertinent pour le défilement
  const getScrollPriority = () => {
    if (isHighlighted) return 10; // Correspondance exacte du chemin
    if (isNameHighlighted && isValueHighlighted) return 8; // Clé et valeur correspondent
    if (isNameHighlighted) return 5; // Clé correspond
    if (isValueHighlighted) return 5; // Valeur correspond
    if (isKeyMatch) return 3; // Correspondance partielle
    return 0;
  };

  // Enregistrer la référence pour le défilement
  useEffect(() => {
    if (shouldSetRef && nodeRef.current) {
      // Ajouter un attribut de priorité pour aider à déterminer quel élément utiliser
      nodeRef.current.setAttribute(
        "data-priority",
        getScrollPriority().toString(),
      );

      setResultRef(resultId, nodeRef.current);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shouldSetRef,
    resultId,
    setResultRef,
    currentPath,
    isHighlighted,
    isNameHighlighted,
    isValueHighlighted,
  ]);

  // Expansion automatique pour la recherche et la mise en surbrillance
  useEffect(() => {
    if (
      (searchTerm &&
        ((typeof data !== "object" &&
          data !== null &&
          String(data).toLowerCase().includes(searchTerm.toLowerCase())) ||
          (name &&
            name
              .toString()
              .toLowerCase()
              .includes(searchTerm.toLowerCase())))) ||
      (highlightPath && currentPath && highlightPath.startsWith(currentPath))
    ) {
      setExpanded(true);
    }
  }, [searchTerm, highlightPath, currentPath, data, name]);

  const toggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  // Fonction améliorée pour gérer le clic sur un chemin
  const handlePathClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (onPathClick && currentPath) {
      onPathClick(currentPath);
    }
  };

  const isObject =
    typeof data === "object" && data !== null && !Array.isArray(data);
  const isArray = Array.isArray(data);

  // Augmentation de l'indentation pour une meilleure lisibilité
  const indentSize = 16; // Augmentation de la taille d'indentation (était 5px)
  const indent = {
    paddingLeft: `${level * indentSize}px`,
    marginLeft: level > 0 ? "4px" : "0",
    borderLeft: level > 0 ? "1px solid #e0e0e0" : "none",
  };

  // Style pour le chemin cliquable avec surbrillance conditionnelle
  const pathStyle = {
    color: "#888",
    marginRight: "5px",
    cursor: "pointer",
    textDecoration: "underline",
    backgroundColor:
      selectedPath === currentPath
        ? "#ffff99"
        : isNameHighlighted
          ? "yellow"
          : "transparent",
    padding: "2px",
    borderRadius: "3px",
    transition: "background-color 0.3s ease",
    display: "inline-block", // Assure que l'élément est cliquable sur toute sa surface
  };

  // Déterminer le style de l'élément principal en fonction de la mise en surbrillance
  const mainStyle = {
    ...indent,
    backgroundColor: isHighlighted ? "rgba(255, 255, 0, 0.2)" : "transparent",
    padding: isHighlighted ? "2px" : "0",
    borderRadius: "3px",
    marginBottom: isObject || isArray ? "4px" : "2px", // Ajoute un espacement vertical
  };

  const renderValue = (value: any) => {
    const isValueMatch =
      isHighlighted ||
      (typeof value !== "object" &&
        value !== null &&
        searchTerm &&
        String(value).toLowerCase().includes(searchTerm.toLowerCase()));

    const highlightStyle = isValueMatch
      ? {
          backgroundColor: "yellow",
          padding: "2px 4px",
          borderRadius: "3px",
        }
      : {};

    if (typeof value === "string") {
      return (
        <span
          style={{
            color: "green",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "inline-block",
            maxWidth: "600px",
            ...highlightStyle,
          }}
          title={value}
        >
          "{value}"
        </span>
      );
    }
    if (typeof value === "number") {
      return (
        <span
          style={{
            color: "blue",
            ...highlightStyle,
          }}
        >
          {value}
        </span>
      );
    }
    if (typeof value === "boolean") {
      return (
        <span
          style={{
            color: "purple",
            ...highlightStyle,
          }}
        >
          {value.toString()}
        </span>
      );
    }
    if (value === null) {
      return (
        <span
          style={{
            color: "red",
            ...highlightStyle,
          }}
        >
          null
        </span>
      );
    }
    return null;
  };

  // Déterminer si cet élément est exactement celui qui correspond au chemin recherché
  const isExactPathMatch = highlightPath === currentPath;

  return (
    <div
      style={mainStyle}
      ref={nodeRef}
      data-path={currentPath}
      data-highlighted={
        isHighlighted || isNameHighlighted || isValueHighlighted
          ? "true"
          : "false"
      }
      data-exact-match={isExactPathMatch ? "true" : "false"}
      data-name-match={isNameHighlighted ? "true" : "false"}
      data-value-match={isValueHighlighted ? "true" : "false"}
    >
      {isObject || isArray ? (
        <div>
          <span style={{ userSelect: "none" }}>
            <span
              onClick={toggleExpand}
              style={{ cursor: "pointer", marginRight: "5px" }}
            >
              {expanded ? "▼" : "▶"}
            </span>
            {name && (
              <span
                onClick={handlePathClick}
                style={pathStyle}
                title="Cliquer pour copier le chemin"
              >
                {name}
              </span>
            )}
            <span>{isObject ? " {" : " ["}</span>
          </span>
          {expanded && (
            <div style={{ marginTop: "2px" }}>
              {Object.entries(data).map(([key, value], index) => {
                const newPath = [...path, isArray ? parseInt(key) : key];
                const newPathStr = newPath
                  .map((k, i) =>
                    typeof k === "number" ? `[${k}]` : i === 0 ? k : `.${k}`,
                  )
                  .join("");

                // Vérifier si ce chemin correspond exactement au chemin mis en évidence
                const isExactMatch = newPathStr === highlightPath;

                // Vérifier si la clé correspond au terme de recherche
                const isKeySearchMatch =
                  searchTerm !== "" &&
                  key
                    .toString()
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase());

                return (
                  <JsonNode
                    key={index}
                    data={value}
                    name={isArray ? `${index}` : key}
                    level={level + 1}
                    path={newPath}
                    searchTerm={searchTerm}
                    highlightPath={highlightPath}
                    resultId={resultId}
                    setResultRef={setResultRef}
                    onPathClick={onPathClick}
                    selectedPath={selectedPath}
                    isKeyMatch={Boolean(isExactMatch || isKeySearchMatch)}
                    isMainSelector={isMainSelector}
                    mainSelectorName={mainSelectorName}
                  />
                );
              })}
              <div
                style={{
                  paddingLeft: `${level * indentSize}px`,
                  marginTop: "2px",
                }}
              >
                {isObject ? "}" : "]"}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div>
          {name && (
            <span
              onClick={handlePathClick}
              style={pathStyle}
              title="Cliquer pour copier le chemin"
            >
              {name}:
            </span>
          )}
          {renderValue(data)}
        </div>
      )}
    </div>
  );
};

export default JsonNode;
